import { AnimatePresence, motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface PasswordCriteriaProps {
  password: string;
  confirmPassword: string;
}

interface CriterionState {
  id: string;
  label: string;
  met: boolean;
  regex?: RegExp;
  customCheck?: (password: string, confirmPassword: string) => boolean;
}

export function PasswordCriteria({
  password,
  confirmPassword,
}: PasswordCriteriaProps) {
  const [criteria, setCriteria] = useState<CriterionState[]>([
    {
      id: 'uppercase',
      label: 'Uppercase letter',
      met: false,
      regex: /[A-Z]/,
    },
    {
      id: 'lowercase',
      label: 'Lowercase letter',
      met: false,
      regex: /[a-z]/,
    },
    {
      id: 'number',
      label: 'Number',
      met: false,
      regex: /[0-9]/,
    },
    {
      id: 'special',
      label: 'Special character',
      met: false,
      regex: /[^A-Za-z0-9]/,
    },
    {
      id: 'length',
      label: '8 characters or more',
      met: false,
      customCheck: (password) => password.length >= 8,
    },
    {
      id: 'match',
      label: 'Passwords match',
      met: false,
      customCheck: (password, confirmPassword) =>
        password === confirmPassword && password.length > 0,
    },
  ]);

  // Update criteria whenever password or confirmPassword changes
  useEffect(() => {
    setCriteria((prevCriteria) =>
      prevCriteria.map((criterion) => {
        if (criterion.regex) {
          return {
            ...criterion,
            met: criterion.regex.test(password),
          };
        }
        if (criterion.customCheck) {
          return {
            ...criterion,
            met: criterion.customCheck(password, confirmPassword),
          };
        }
        return criterion;
      })
    );
  }, [password, confirmPassword]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-2 text-sm"
      >
        <p className="sr-only">Password must meet the following criteria:</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {criteria.map((criterion, index) => (
            <motion.div
              key={criterion.id}
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05, duration: 0.2 }}
              className="flex items-center gap-2"
            >
              <motion.div
                initial={false}
                animate={{
                  backgroundColor: criterion.met
                    ? 'rgb(34, 197, 94)'
                    : 'rgb(239, 68, 68)',
                  scale: criterion.met ? [1, 1.2, 1] : 1,
                }}
                transition={{ duration: 0.2 }}
                className="flex items-center justify-center w-4 h-4 rounded-full"
              >
                <AnimatePresence mode="wait">
                  {criterion.met ? (
                    <motion.div
                      key="check"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Check className="w-3 h-3 text-white" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="x"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <X className="w-3 h-3 text-white" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              <motion.span
                animate={{
                  color: criterion.met
                    ? 'rgb(34, 197, 94)'
                    : 'rgb(156, 163, 175)',
                }}
                transition={{ duration: 0.2 }}
                className="text-xs"
              >
                {criterion.label}
              </motion.span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
